import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { UploadSimple, FileCsv, ChartBar, ShieldCheck } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = '/api';

const LeadIntakeCenter = () => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [lastReport, setLastReport] = useState(null);
  const [reports, setReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);

  const fetchReports = async () => {
    try {
      const { data } = await axios.get(`${API}/intake/reports`, { withCredentials: true });
      setReports(data || []);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    fetchReports();
  }, []);

  const uploadFile = async () => {
    if (!file) {
      toast.error('Selecione um arquivo CSV ou XLSX');
      return;
    }

    const form = new FormData();
    form.append('file', file);

    setUploading(true);
    try {
      const { data } = await axios.post(`${API}/intake/upload-leads`, form, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setLastReport(data?.report || null);
      toast.success('Importação processada com sucesso');
      fetchReports();
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || 'Erro ao importar leads');
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = () => {
    const csv = 'nome,telefone,email,origem,empreendimento,observacoes\nMaria Silva,(77) 99988-7766,maria@email.com,Facebook,Dona Residencial,Interessada no empreendimento\n';
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'modelo_importacao_leads.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const fmt = (v) => Number(v || 0).toLocaleString('pt-BR');

  const latest = lastReport || reports?.[0] || null;

  const utilizationRate = latest?.total_received
    ? Math.round(((latest.total_imported || 0) / latest.total_received) * 100)
    : 0;

  const qualityStatus =
    utilizationRate >= 85 ? 'Base Comercial Validada' :
    utilizationRate >= 70 ? 'Base com Atenção' :
    latest ? 'Base Crítica' : 'Aguardando importação';

  const qualityColor =
    utilizationRate >= 85 ? 'text-emerald-700 bg-emerald-50' :
    utilizationRate >= 70 ? 'text-amber-700 bg-amber-50' :
    latest ? 'text-red-700 bg-red-50' : 'text-slate-700 bg-slate-50';

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Lead Intake Center</h1>
            <p className="text-slate-600 mt-1">
              Centro de entrada inteligente de leads com validação, deduplicação, conflitos comerciais e diagnóstico da qualidade da base.
            </p>
          </div>
          <div className="flex flex-wrap gap-2 justify-end">
            <a
              href="/performance-comercial"
              className="px-4 py-2 rounded-lg bg-white border border-slate-300 text-slate-800 font-semibold hover:bg-slate-50"
            >
              Voltar para Performance Comercial
            </a>

            <a
              href="/activation-control"
              className="px-4 py-2 rounded-lg bg-indigo-600 text-white font-semibold hover:bg-indigo-700"
            >
              Bases aguardando ativação
            </a>

            <button
              onClick={downloadTemplate}
              className="px-4 py-2 rounded-lg bg-slate-900 text-white font-semibold hover:bg-slate-800"
            >
              Baixar modelo CSV
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
            <ShieldCheck size={28} className="text-emerald-600 mb-3" />
            <p className="text-sm text-slate-500">Validator Engine</p>
            <h3 className="text-xl font-bold text-slate-900">Lead contactável</h3>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
            <FileCsv size={28} className="text-blue-600 mb-3" />
            <p className="text-sm text-slate-500">Formatos</p>
            <h3 className="text-xl font-bold text-slate-900">CSV / XLSX</h3>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
            <ChartBar size={28} className="text-purple-600 mb-3" />
            <p className="text-sm text-slate-500">Qualidade da base</p>
            <h3 className="text-xl font-bold text-slate-900">Relatório automático</h3>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
            <UploadSimple size={28} className="text-slate-700 mb-3" />
            <p className="text-sm text-slate-500">Intelligence</p>
            <h3 className="text-xl font-bold text-slate-900">Recalcula IPC/MDI/DMB</h3>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-5 border-b border-slate-200 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <h2 className="font-bold text-slate-900">Qualidade da Base Comercial</h2>
              <p className="text-sm text-slate-500">
                Leitura executiva da última importação processada pelo Lead Intake Center.
              </p>
            </div>
            <span className={`px-4 py-2 rounded-full text-sm font-bold ${qualityColor}`}>
              {qualityStatus}
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 p-5">
            <div className="rounded-xl border border-slate-200 p-4">
              <p className="text-sm text-slate-500">Recebidos</p>
              <p className="text-2xl font-bold text-slate-900">{fmt(latest?.total_received)}</p>
            </div>
            <div className="rounded-xl border border-slate-200 p-4">
              <p className="text-sm text-slate-500">Importados</p>
              <p className="text-2xl font-bold text-emerald-700">{fmt(latest?.total_imported)}</p>
            </div>
            <div className="rounded-xl border border-slate-200 p-4">
              <p className="text-sm text-slate-500">Duplicados</p>
              <p className="text-2xl font-bold text-blue-700">{fmt(latest?.total_duplicated)}</p>
            </div>
            <div className="rounded-xl border border-slate-200 p-4">
              <p className="text-sm text-slate-500">Conflitos</p>
              <p className="text-2xl font-bold text-amber-700">{fmt(latest?.total_conflicts)}</p>
            </div>
            <div className="rounded-xl border border-slate-200 p-4">
              <p className="text-sm text-slate-500">Descartados</p>
              <p className="text-2xl font-bold text-red-700">{fmt(latest?.total_discarded)}</p>
            </div>
          </div>

          <div className="px-5 pb-5 grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="rounded-xl bg-slate-50 p-4">
              <p className="text-sm font-semibold text-slate-700">Taxa de aproveitamento</p>
              <div className="flex items-end gap-2 mt-2">
                <span className="text-3xl font-bold text-slate-900">{latest ? utilizationRate : 0}%</span>
                <span className="text-sm text-slate-500 mb-1">da base recebida</span>
              </div>
              <div className="h-2 bg-slate-200 rounded-full overflow-hidden mt-3">
                <div
                  className="h-full bg-slate-900 rounded-full"
                  style={{ width: `${Math.min(100, Math.max(0, utilizationRate))}%` }}
                />
              </div>
            </div>

            <div className="rounded-xl bg-slate-50 p-4">
              <p className="text-sm font-semibold text-slate-700">Motivos de descarte</p>
              {latest?.discard_reasons && Object.keys(latest.discard_reasons).length > 0 ? (
                <div className="mt-3 space-y-2">
                  {Object.entries(latest.discard_reasons).map(([reason, count]) => (
                    <div key={reason} className="flex justify-between gap-3 text-sm">
                      <span className="text-slate-600">{reason}</span>
                      <span className="font-bold text-red-700">{fmt(count)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500 mt-3">Nenhum descarte registrado.</p>
              )}
            </div>

            <div className="rounded-xl bg-slate-50 p-4">
              <p className="text-sm font-semibold text-slate-700">Status operacional</p>
              {latest ? (
                <ul className="mt-3 space-y-1 text-sm text-slate-600">
                  <li>✓ Arquivo recebido</li>
                  <li>✓ Contactabilidade validada</li>
                  <li>✓ Duplicidade verificada</li>
                  <li>✓ Conflitos comerciais analisados</li>
                  <li>✓ Orkestry Intelligence recalculado</li>
                </ul>
              ) : (
                <p className="text-sm text-slate-500 mt-3">
                  Faça a primeira importação para gerar o diagnóstico da base.
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
          <h2 className="text-lg font-bold text-slate-900 mb-2">Importar leads</h2>
          <p className="text-sm text-slate-500 mb-5">
            Somente leads contactáveis serão importados.
          </p>

          <div className="border-2 border-dashed border-slate-300 rounded-xl p-5 text-center">
            <UploadSimple size={32} className="mx-auto text-slate-500 mb-2" />
            <input
              type="file"
              accept=".csv,.xlsx"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="block mx-auto"
            />
            {file && (
              <p className="text-sm text-slate-600 mt-3">
                Arquivo selecionado: <span className="font-semibold">{file.name}</span>
              </p>
            )}
            <button
              onClick={uploadFile}
              disabled={uploading}
              className="mt-4 px-5 py-2 rounded-lg bg-indigo-600 text-white font-semibold hover:bg-indigo-700 disabled:opacity-60"
            >
              {uploading ? 'Processando...' : 'Processar importação'}
            </button>
          </div>
        </div>

        {lastReport && (
          <div className="bg-gradient-to-br from-indigo-600 to-purple-600 rounded-xl p-6 text-white shadow-sm">
            <p className="text-sm text-indigo-100 mb-2">Base comercial validada</p>
            <h2 className="text-2xl font-bold">{lastReport.filename}</h2>
            <p className="text-indigo-100 mt-2">
              {fmt(lastReport.total_imported)} leads prontos para operação após validação, deduplicação e análise de conflitos.
            </p>
          </div>
        )}

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-5 border-b border-slate-200">
            <h2 className="font-bold text-slate-900">Log Executivo de Importações</h2>
            <p className="text-sm text-slate-500">
              Registro das importações processadas, com status executivo e acesso aos detalhes.
            </p>
          </div>

          <div className="divide-y divide-slate-100">
            {reports.length === 0 ? (
              <div className="px-5 py-6 text-center text-slate-500">
                Nenhuma importação registrada.
              </div>
            ) : (
              reports.map((r, i) => {
                const rate = r.total_received ? Math.round(((r.total_imported || 0) / r.total_received) * 100) : 0;
                const status =
                  rate >= 85 ? 'Base aprovada' :
                  rate >= 70 ? 'Base com atenção' :
                  'Base crítica';

                const badge =
                  rate >= 85 ? 'bg-emerald-50 text-emerald-700' :
                  rate >= 70 ? 'bg-amber-50 text-amber-700' :
                  'bg-red-50 text-red-700';

                return (
                  <button
                    key={i}
                    onClick={() => setSelectedReport(r)}
                    className="w-full px-5 py-4 text-left hover:bg-slate-50 transition grid grid-cols-1 md:grid-cols-12 gap-3 items-center"
                  >
                    <div className="md:col-span-5">
                      <p className="font-semibold text-slate-900">{r.filename}</p>
                      <p className="text-xs text-slate-500">
                        {r.created_at ? new Date(r.created_at).toLocaleString('pt-BR') : 'Data não informada'}
                      </p>
                    </div>

                    <div className="md:col-span-3 text-sm text-slate-600">
                      {fmt(r.total_imported)} importados de {fmt(r.total_received)} recebidos
                    </div>

                    <div className="md:col-span-2">
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${badge}`}>
                        {status}
                      </span>
                    </div>

                    <div className="md:col-span-2 text-right text-sm font-semibold text-slate-700">
                      {rate}% aproveitamento
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {selectedReport && (
          <div className="fixed inset-0 bg-slate-900/40 z-50 flex justify-end">
            <div className="w-full max-w-md bg-white h-full shadow-xl p-6 overflow-y-auto">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-xl font-bold text-slate-900">Detalhes da importação</h2>
                  <p className="text-sm text-slate-500 mt-1">{selectedReport.filename}</p>
                </div>
                <button
                  onClick={() => setSelectedReport(null)}
                  className="px-3 py-1 rounded-lg bg-slate-100 text-slate-700 font-semibold"
                >
                  Fechar
                </button>
              </div>

              <div className="grid grid-cols-2 gap-3 mt-6">
                <div className="rounded-xl border border-slate-200 p-4">
                  <p className="text-xs text-slate-500">Recebidos</p>
                  <p className="text-2xl font-bold text-slate-900">{fmt(selectedReport.total_received)}</p>
                </div>
                <div className="rounded-xl border border-slate-200 p-4">
                  <p className="text-xs text-slate-500">Importados</p>
                  <p className="text-2xl font-bold text-emerald-700">{fmt(selectedReport.total_imported)}</p>
                </div>
                <div className="rounded-xl border border-slate-200 p-4">
                  <p className="text-xs text-slate-500">Duplicados</p>
                  <p className="text-2xl font-bold text-blue-700">{fmt(selectedReport.total_duplicated)}</p>
                </div>
                <div className="rounded-xl border border-slate-200 p-4">
                  <p className="text-xs text-slate-500">Conflitos</p>
                  <p className="text-2xl font-bold text-amber-700">{fmt(selectedReport.total_conflicts)}</p>
                </div>
                <div className="rounded-xl border border-slate-200 p-4 col-span-2">
                  <p className="text-xs text-slate-500">Descartados</p>
                  <p className="text-2xl font-bold text-red-700">{fmt(selectedReport.total_discarded)}</p>
                </div>
              </div>

              <div className="mt-6 rounded-xl bg-slate-50 p-4">
                <p className="text-sm font-bold text-slate-700">Motivos de descarte</p>
                {selectedReport.discard_reasons && Object.keys(selectedReport.discard_reasons).length > 0 ? (
                  <div className="mt-3 space-y-2">
                    {Object.entries(selectedReport.discard_reasons).map(([reason, count]) => (
                      <div key={reason} className="flex justify-between gap-3 text-sm">
                        <span className="text-slate-600">{reason}</span>
                        <span className="font-bold text-red-700">{fmt(count)}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500 mt-3">Nenhum descarte registrado.</p>
                )}
              </div>

              <div className="mt-6 rounded-xl bg-slate-50 p-4">
                <p className="text-sm font-bold text-slate-700">Timeline operacional</p>
                <ul className="mt-3 space-y-2 text-sm text-slate-600">
                  <li>✓ Arquivo recebido</li>
                  <li>✓ Linhas lidas</li>
                  <li>✓ Contactabilidade validada</li>
                  <li>✓ Duplicidades verificadas</li>
                  <li>✓ Conflitos comerciais analisados</li>
                  <li>✓ Relatório de importação gerado</li>
                  <li>✓ Orkestry Intelligence recalculado</li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default LeadIntakeCenter;
